import {
  Component,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import {
  DynamicDialogRef,
  DynamicDialogConfig,
  DialogService,
} from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { UserRegistrationService } from '../../../../services/user-registration.service';
import { SendOtpRequest, VerifyOtpRequest } from '../../forms/forms.model';
import { ToastModule } from 'primeng/toast';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { NewPasswordDialog } from '../../../settings/new-password-dialog/new-password-dialog';
import { LoaderService } from '../../../../services/loader.service';
import { accountDeletedDialog } from '../../../settings/delete-ac/account-deleted.dialog';

@Component({
  selector: 'app-email-verificn-otp-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
  ],
  templateUrl: './email-verificn-otp-dialog.html',
  styleUrls: ['./email-verificn-otp-dialog.scss'],
  providers: [MessageService, DialogService],
})
export class EmailVerificnOtpDialog implements AfterViewInit {
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private messageService = inject(MessageService);
  private loaderService = inject(LoaderService);
  private router = inject(Router);
  private userService = inject(UserRegistrationService);
  isFromSettings = this.config.data?.isFromSettings || false;
  private dialogService = inject(DialogService);
  isFromDeleteAccount = this.config.data?.isFromDeleteAccount || false;
  isFromCancelSubscription = this.config.data?.isFromCancelSubscription || false;
  deleteReasonData: { reason: string; otherInfo: string | null } = {
    reason: '',
    otherInfo: null,
  };

  @ViewChild('otp1Input') otp1Input!: ElementRef<HTMLInputElement>;
  @ViewChild('otp2Input') otp2Input!: ElementRef<HTMLInputElement>;
  @ViewChild('otp3Input') otp3Input!: ElementRef<HTMLInputElement>;
  @ViewChild('otp4Input') otp4Input!: ElementRef<HTMLInputElement>;
  @ViewChild('otp5Input') otp5Input!: ElementRef<HTMLInputElement>;
  @ViewChild('otp6Input') otp6Input!: ElementRef<HTMLInputElement>;

  email = this.config.data?.email || 'xxx@gmail.com';
  userId = this.config.data?.userId || 0;
  isNewUser = this.config.data?.isNewUser || true;
  isAddVenueFlow = this.config.data?.isAddVenueFlow || false;

  otp1 = '';
  otp2 = '';
  otp3 = '';
  otp4 = '';
  otp5 = '';
  otp6 = '';
  formSubmitted = false;

  ngOnInit() {
    this.email = this.config.data?.email || '';
    this.deleteReasonData = this.config.data?.deleteReasonData || {
      reason: '',
      otherInfo: null,
    };

    console.log('Email received:', this.email);
    console.log('Delete reason received:', this.deleteReasonData);
    const pendingLogout = localStorage.getItem('pendingLogout');
    if (pendingLogout === 'true') {
      // Clear everything to force logout even after manual refresh
      localStorage.clear();
      sessionStorage.clear();

      // Optionally clear cookies
      document.cookie.split(';').forEach((c) => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      this.router.navigate(['/sign-in']).then(() => {
        window.location.reload();
      });
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.otp1Input?.nativeElement.focus();
    }, 100);
  }

  navigateBackToForm(): void {
    this.ref.close('back');
    this.router.navigate(['/signup']);
  }

  resendCode() {
    const request: SendOtpRequest = {
      UserType: 'Business',
      Email: this.email,
    };
    if (this.isFromSettings) {
      this.userService
        .sendEmailOtpChangePassword(request)
        .pipe(
          catchError((error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'error',
              detail: 'failed to resend code. please try again.',
              life: 3000,
            });
            return of(null);
          })
        )
        .subscribe((response) => {
          if (response) {
            this.messageService.add({
              severity: 'success',
              summary: 'code resent',
              detail: `new verification code sent to ${this.email}`,
              life: 3000,
            });
            this.clearOtp();
            this.otp1Input?.nativeElement.focus();
          }
        });
    } else {
      this.userService
        .sendEmailOtp(request)
        .pipe(
          catchError((error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'error',
              detail: 'failed to resend code. please try again.',
              life: 3000,
            });
            return of(null);
          })
        )
        .subscribe((response) => {
          if (response) {
            this.messageService.add({
              severity: 'success',
              summary: 'code resent',
              detail: `new verification code sent to ${this.email}`,
              life: 3000,
            });
            this.clearOtp();
            this.otp1Input?.nativeElement.focus();
          }
        });
    }
  }

  private clearOtp() {
    this.otp1 = this.otp2 = this.otp3 = this.otp4 = this.otp5 = this.otp6 = '';
  }

  navigatetoProfile(): void {
    this.formSubmitted = true;
    const fullOtp =
      this.otp1 + this.otp2 + this.otp3 + this.otp4 + this.otp5 + this.otp6;

    // VALIDATION
    if (fullOtp.length !== 6) {
      this.messageService.add({
        severity: 'error',
        summary: 'invalid code',
        detail: 'please enter all 6 digits',
        life: 3000,
      });
      this.formSubmitted = false;
      return;
    }

    const verifyRequest: VerifyOtpRequest = {
      Email: this.email,
      OTP: fullOtp,
    };

    this.userService
      .verifyEmailOtp(verifyRequest)
      .pipe(
        catchError((error: any) => {
          let errorMessage = 'Network error. Please try again.';

          if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'connection error',
            detail: errorMessage,
            life: 4000,
          });

          return of(null);
        }),
        finalize(() => {
          this.formSubmitted = false;
        })
      )
      .subscribe((response) => {
        if (response && response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'verified!',
            detail: response.data?.message || 'email verified successfully',
            life: 2000,
          });

          if (this.isFromCancelSubscription) {
            this.ref.close({ verified: true });
            return;
          }

          if (this.isFromDeleteAccount) {
            const deletePayload = {
              Email: this.email,
              Reason:
                this.deleteReasonData.reason === 'other'
                  ? null
                  : this.deleteReasonData.reason,
              OtherInfo: this.deleteReasonData.otherInfo,
            };

            this.loaderService.show();

            this.userService.deleteUser(deletePayload).subscribe({
              next: (res) => {
                this.loaderService.hide();

                if (res.success) {
                  this.ref.close();
                  const dialogRef = this.dialogService.open(
                    accountDeletedDialog,
                    {
                      header: 'Delete Account',
                      width: 'auto',
                      height: 'auto',
                      styleClass: 'custom-dialog',
                    }
                  );
                  localStorage.setItem('pendingLogout', 'true');

                  // Save pending logout marker
                  localStorage.setItem('pendingLogout', 'true');

                  setTimeout(() => {
                    dialogRef.close();
                    localStorage.clear();
                    sessionStorage.clear();

                    document.cookie.split(';').forEach((c) => {
                      document.cookie = c
                        .replace(/^ +/, '')
                        .replace(
                          /=.*/,
                          '=;expires=' + new Date().toUTCString() + ';path=/'
                        );
                    });

                    this.router.navigate(['/sign-in']).then(() => {
                      window.location.reload();
                    });
                  }, 3000);
                } else {
                  this.messageService.add({
                    severity: 'error',
                    summary: 'error',
                    detail: res.message || 'failed to delete account.',
                  });
                }
              },
              error: (err) => {
                this.loaderService.hide();
                this.messageService.add({
                  severity: 'error',
                  summary: 'error',
                  detail: err?.error?.message || 'failed to delete account.',
                });
              },
            });

            return;
          }

          // if (this.isFromSettings) {
          //   this.ref.close();
          //   this.dialogService.open(NewPasswordDialog, {
          //     data: { email: this.email,userId: response.data?.userId },
          //     modal: true,
          //   });
          // }
          if (this.isFromSettings) {
            this.ref.close();
            this.dialogService.open(NewPasswordDialog, {
              data: {
                email: this.email,
                userId: response.data?.userId,
              },
              modal: true,
            });
          } else {
            this.ref.close({
              otp: fullOtp,
              email: this.email,
              userId: this.userId,
              isNewUser: this.isNewUser,
              verified: true,
            });
            // Navigate with addVenue param if in add-venue flow
            if (this.isAddVenueFlow) {
              this.router.navigate(['/sign-up/profile'], { queryParams: { addVenue: 'true' } });
            } else {
              this.router.navigate(['/sign-up/profile']);
            }
          }
          return;
        }

        if (response && !response.success) {
          let detail = response.message || 'verification failed';

          if (response.data?.message) {
            detail = response.data.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'verification failed',
            detail: detail,
            life: 4000,
          });
        }
      });
  }

  focusNext(event: any, nextInputElement: HTMLInputElement | null) {
    if (event.target.value.length === 1 && nextInputElement) {
      nextInputElement.focus();
    }
  }

  handleKeydown(
    event: KeyboardEvent,
    prevInputElement: HTMLInputElement | null
  ) {
    const target = event.currentTarget as HTMLInputElement;
    if (event.key === 'Backspace' && !target.value && prevInputElement) {
      prevInputElement.focus();
    }
  }
  onReasonSelected(data: { reason: string; otherInfo: string | null }) {
    this.deleteReasonData = data;
  }
  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    const digits = pasteData.replace(/\D/g, '').slice(0, 6);

    if (digits.length === 6) {
      this.otp1 = digits[0];
      this.otp2 = digits[1];
      this.otp3 = digits[2];
      this.otp4 = digits[3];
      this.otp5 = digits[4];
      this.otp6 = digits[5];
      this.otp6Input.nativeElement.focus();
    }
  }

}
